import React, { useState } from "react";
import { Trash2, ChevronRight } from "lucide-react";
import DoseDialog from "@/components/dialogs/DoseDialog";
import DurationDialog from "@/components/dialogs/DurationDialog";

const FOOD_OPTIONS = [
  { key: "BF", label: "Before food" },
  { key: "AF", label: "After food" },
  { key: "anytime", label: "Anytime" },
];
const MINS = [0, 15, 30, 45, 60];

/**
 * MedicationRow — single drug editor.
 * value = MedicationItem, onChange, onRemove, index for testids
 */
export default function MedicationRow({ value, onChange, onRemove, index }) {
  const [doseOpen, setDoseOpen] = useState(false);
  const [durOpen, setDurOpen] = useState(false);

  const strengths = value.strengths || (value.strength ? [value.strength] : []);
  const doseSummary = () => {
    const d = value.dose;
    if (!d) return "+ Customize";
    return `${d.morning || 0}-${d.afternoon || 0}-${d.night || 0}`;
  };
  const durationSummary = () => {
    if (!value.duration || !value.duration_unit) return "+ Customize";
    return `${value.duration} ${value.duration_unit}${value.duration > 1 ? "s" : ""}`;
  };
  const whenSummary = () => {
    if (!value.when_food) return "Anytime";
    if (value.when_food === "anytime") return "Anytime";
    const mins = value.mins_food ? `${value.mins_food} min ` : "";
    return `${mins}${value.when_food === "AF" ? "After food" : "Before food"}`;
  };

  return (
    <div className="bg-white rounded-2xl p-3 md:p-4 border border-[#EFECE5]" data-testid={`med-row-${index}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {/* Name: editable input styled to look like the section heading.
              For catalogued drugs (with medicine_id) the name is fixed — show as read-only text.
              For custom drugs (no id) the doctor types the name in-place. */}
          {value.medicine_id ? (
            <div className="font-semibold text-[#1F2A24] text-base leading-snug">
              {value.name} {value.form && <span className="text-[#8A9992] font-normal text-sm">({value.form})</span>}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                value={value.name || ""}
                onChange={(e) => onChange({ ...value, name: e.target.value })}
                placeholder="Drug name (e.g. Amoxicillin)"
                autoFocus={!value.name}
                className="flex-1 min-w-0 bg-transparent border-b border-dashed border-[#0A4C36]/30 focus:border-[#0A4C36] focus:outline-none font-semibold text-[#1F2A24] text-base leading-snug py-0.5"
                data-testid={`med-name-${index}`}
              />
              <select
                value={value.form || ""}
                onChange={(e) => onChange({ ...value, form: e.target.value })}
                className="input-clean text-xs py-1.5 w-24 shrink-0"
                data-testid={`med-form-${index}`}
              >
                <option value="">Form</option>
                <option value="Tablet">Tablet</option>
                <option value="Capsule">Capsule</option>
                <option value="Syrup">Syrup</option>
                <option value="Drops">Drops</option>
                <option value="Ointment">Ointment</option>
                <option value="Injection">Injection</option>
                <option value="Inhaler">Inhaler</option>
                <option value="Other">Other</option>
              </select>
            </div>
          )}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            {/* Strength */}
            {strengths.length > 0 ? (
              <select
                value={value.strength || ""}
                onChange={(e) => onChange({ ...value, strength: e.target.value })}
                className="input-clean text-xs py-2"
                data-testid={`med-strength-${index}`}
              >
                <option value="">Strength</option>
                {strengths.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input
                value={value.strength || ""}
                onChange={(e) => onChange({ ...value, strength: e.target.value })}
                placeholder="Strength (e.g. 500 mg)"
                className="input-clean text-xs py-2"
                data-testid={`med-strength-${index}`}
              />
            )}
            {/* Dose (M/A/N) */}
            <button type="button" onClick={() => setDoseOpen(true)} className="input-clean text-xs py-2 text-left flex items-center justify-between" data-testid={`med-dose-btn-${index}`}>
              <span>Dose: <span className="font-semibold text-[#0A4C36]">{doseSummary()}</span></span>
              <ChevronRight size={12} className="text-[#8A9992]" />
            </button>
            {/* Duration */}
            <button type="button" onClick={() => setDurOpen(true)} className="input-clean text-xs py-2 text-left flex items-center justify-between" data-testid={`med-dur-btn-${index}`}>
              <span>Duration: <span className="font-semibold text-[#0A4C36]">{durationSummary()}</span></span>
              <ChevronRight size={12} className="text-[#8A9992]" />
            </button>
          </div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
            <select
              value={value.when_food || "anytime"}
              onChange={(e) => onChange({ ...value, when_food: e.target.value })}
              className="input-clean text-xs py-2"
              data-testid={`med-food-${index}`}
            >
              {FOOD_OPTIONS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <select
              value={value.mins_food ?? 0}
              onChange={(e) => onChange({ ...value, mins_food: Number(e.target.value) })}
              className="input-clean text-xs py-2"
              disabled={!value.when_food || value.when_food === "anytime"}
              data-testid={`med-mins-${index}`}
            >
              {MINS.map((m) => <option key={m} value={m}>{m === 0 ? "No offset" : `${m} min`}</option>)}
            </select>
            <input
              value={value.instructions || ""}
              onChange={(e) => onChange({ ...value, instructions: e.target.value })}
              placeholder="Instructions"
              className="input-clean text-xs py-2"
              data-testid={`med-instructions-${index}`}
            />
          </div>
        </div>
        <button type="button" onClick={onRemove} className="w-8 h-8 rounded-full hover:bg-red-50 text-red-500 flex items-center justify-center shrink-0" data-testid={`med-remove-${index}`}>
          <Trash2 size={14} />
        </button>
      </div>
      <div className="mt-2 text-[11px] text-[#8A9992]">
        Summary: <span className="text-[#47554D]">{doseSummary()} · {whenSummary()} · {durationSummary()}</span>
      </div>

      <DoseDialog open={doseOpen} onOpenChange={setDoseOpen}
        value={value.dose || { morning: 0, afternoon: 0, night: 0 }}
        onChange={(d) => onChange({ ...value, dose: d })} />

      <DurationDialog open={durOpen} onOpenChange={setDurOpen}
        value={{ duration: value.duration || 3, duration_unit: value.duration_unit || "day", qty: value.qty || 0 }}
        onChange={(u) => onChange({ ...value, duration: u.duration, duration_unit: u.duration_unit, qty: u.qty })} />
    </div>
  );
}

export function medFromMedicine(m) {
  return {
    medicine_id: m.id,
    name: m.name,
    form: m.form,
    strengths: m.strengths,
    strength: (m.strengths && m.strengths[0]) || "",
    dose: { morning: 0, afternoon: 0, night: 1 },
    when_food: "AF",
    mins_food: 0,
    duration: 3,
    duration_unit: "day",
    qty: 0,
    instructions: "",
  };
}

export function emptyMed() {
  return {
    name: "",
    form: "",
    strengths: [],
    strength: "",
    dose: { morning: 0, afternoon: 0, night: 1 },
    when_food: "AF",
    mins_food: 0,
    duration: 3,
    duration_unit: "day",
    qty: 0,
    instructions: "",
  };
}
